<template>
  <component
    :is="tag"
    :class="[
      'tw:m-0 tw:p-0 tw:rounded-[4px]',
      skeleton && 'skeleton-play',
      disabled && 'tw:opacity-50 tw:cursor-not-allowed tw:pointer-events-none',
      lineClampClass,
    ]"
    :style="textStyle"
    @click="emit('click')"
  >
    <slot>{{ text }}</slot>
  </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

const emit = defineEmits<{
  click: []
}>()

const props = withDefaults(defineProps<{
  tag?: 'a' | 'p' | 'span' | 'label' | 'strong' | 'div' | 'h1'
  text?: string | number
  size?: 'xxs' | 'xs' | 'sm' | 'base' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | '8xl' | '9xl'
  color?: string
  weight?: 'thin' | 'extralight' | 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold' | 'black'
  skeleton?: boolean
  lineClamp?: number | string
  disabled?: boolean
  decoration?: 'none' | 'underline' | 'line-through'
}>(), {
  tag: 'p',
  text: undefined,
  size: 'base',
  color: 'var(--fontcolor)',
  weight: undefined,
  skeleton: false,
  lineClamp: undefined,
  disabled: false,
  decoration: 'none',
})

const FONT_SIZES: Record<string, string> = {
  xxs: '0.625rem',
  xs: '0.75rem',
  sm: '0.875rem',
  base: '1rem',
  md: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem',
  '5xl': '3rem',
  '6xl': '3.75rem',
  '7xl': '4.5rem',
  '8xl': '6rem',
  '9xl': '8rem',
}

const LINE_HEIGHTS: Record<string, string> = {
  xxs: '1rem',
  xs: '1rem',
  sm: '1.25rem',
  base: '1.5rem',
  md: '1.5rem',
  lg: '1.75rem',
  xl: '1.75rem',
  '2xl': '2rem',
  '3xl': '2.25rem',
  '4xl': '2.5rem',
  '5xl': '1',
  '6xl': '1',
  '7xl': '1',
  '8xl': '1',
  '9xl': '1',
}

const WEIGHTS: Record<string, string> = {
  thin: '100',
  extralight: '200',
  light: '300',
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
  extrabold: '800',
  black: '900',
}

const textStyle = computed(() => {
  const s: Record<string, string> = {
    fontSize: FONT_SIZES[props.size] ?? '1rem',
    lineHeight: LINE_HEIGHTS[props.size] ?? '1.5rem',
    color: props.color,
    fontWeight: props.weight ? (WEIGHTS[props.weight] ?? '400') : '400',
  }
  if (props.decoration !== 'none') {
    s.textDecoration = props.decoration
  }
  if (props.lineClamp && !lineClampClass.value) {
    s['--line-clamp'] = String(props.lineClamp)
  }
  return s
})

const lineClampClass = computed(() => {
  if (!props.lineClamp) return undefined
  const n = Number(props.lineClamp)
  if (n >= 1 && n <= 6) return `tw:line-clamp-${n}`
  return 'text-play-line-clamp'
})
</script>

<style scoped>
.text-play-line-clamp {
  display: -webkit-box;
  -webkit-line-clamp: var(--line-clamp);
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
